import { Handle, Position, type NodeProps } from "@xyflow/react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { Bot, ClipboardList, ListPlus, MessageSquare, Zap } from "lucide-mui";
import { useContext, type ReactNode } from "react";
import { NODE_HEIGHT, NODE_WIDTH } from "./layout.ts";
import { SubjectsContext } from "./subjects.ts";
import {
  CONTRACT_LABELS,
  KIND_META,
  SUBJECT_LABELS,
  TRIGGER_ID,
  triggerLabel,
  type StepData,
  type StepKind,
  type StepNode as StepNodeType,
} from "./types.ts";
import { scheduleLabel } from "../schedule.ts";

export const KIND_ICONS: Record<StepKind, ReactNode> = {
  trigger: <Zap fontSize="small" />,
  agent: <Bot fontSize="small" />,
  add_to_prospect_list: <ListPlus fontSize="small" />,
  slack_message: <MessageSquare fontSize="small" />,
  create_prospect_order: <ClipboardList fontSize="small" />,
};

export function nodeLabel(data: StepData): string {
  if (data.kind === "trigger") return triggerLabel(data);
  if (data.kind === "agent" && data.agent_name) return data.agent_name;
  return KIND_META[data.kind].label;
}

export function summarize(data: StepData): string {
  switch (data.kind) {
    case "trigger":
      if (data.trigger_type === "prospect_order") return "Every prospect order placed for this tenant";
      if (data.trigger_type === "schedule") return scheduleLabel(data.schedule);
      // The label already names the data type ("Notion page trigger").
      if (!data.data_type) return "No data type";
      return data.prompt.trim() || "No description yet";
    case "agent": {
      if (!data.agent_id) return "No agent selected";
      const what =
        data.instructions.trim() ||
        (data.contract ? CONTRACT_LABELS[data.contract] : "Works as the agent is defined");
      return data.max_runs_per_day != null ? `${what} · ≤ ${data.max_runs_per_day}/day` : what;
    }
    case "add_to_prospect_list":
      return data.collection_id ? data.collection_name || "Unknown collection" : "No collection selected";
    case "slack_message":
      return data.channel_id ? `#${data.channel_name || data.channel_id}` : "No channel selected";
    case "create_prospect_order": {
      const parts = [data.on_repeat === "new" ? "New order per signal" : "One order per page"];
      if (data.defaults.requested_count != null) parts.push(`${data.defaults.requested_count} companies`);
      return parts.join(" · ");
    }
  }
}

const handleStyle = (accent: string) => ({
  width: 10,
  height: 10,
  background: accent,
  border: "2px solid var(--mui-palette-background-paper)",
});

export default function StepNode({ id, data, selected }: NodeProps<StepNodeType>) {
  const meta = KIND_META[data.kind];
  const inactive = data.kind !== "trigger" && !data.enabled;
  // Under "Create prospect order" the steps run on the order, not the signal.
  const subjects = useContext(SubjectsContext);
  const subject = subjects.get(id);
  const switched = data.kind !== "trigger" && subject && subject !== subjects.get(TRIGGER_ID);

  return (
    <Box
      sx={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        boxSizing: "border-box",
        px: 1.5,
        py: 1,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: selected ? "primary.main" : "var(--mui-palette-divider)",
        borderLeft: `4px solid ${meta.accent}`,
        bgcolor: "var(--mui-palette-background-paper)",
        boxShadow: selected
          ? "0 0 0 3px rgba(var(--mui-palette-primary-mainChannel) / 0.25)"
          : "0 1px 2px rgba(var(--mui-palette-text-primaryChannel) / 0.06)",
        opacity: inactive ? 0.6 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        outline: "none",
        cursor: "pointer",
        transition: "box-shadow 120ms, border-color 120ms",
      }}
    >
      {meta.hasInput && (
        <Handle id="in" type="target" position={Position.Top} style={handleStyle(meta.accent)} />
      )}
      {data.kind === "agent" && (
        // Where the data-source pills attach (dataSources.ts); never user-connectable.
        <Handle
          id="data"
          type="target"
          position={Position.Left}
          isConnectable={false}
          style={{
            width: 6,
            height: 6,
            minWidth: 0,
            minHeight: 0,
            background: "var(--mui-palette-background-paper)",
            border: "1px solid var(--mui-palette-text-disabled)",
          }}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: meta.accent }}>
        {KIND_ICONS[data.kind]}
        <Typography
          variant="overline"
          sx={{ lineHeight: 1.4, fontSize: "0.65rem", fontWeight: 700, color: "text.secondary", flexGrow: 1 }}
          noWrap
        >
          {nodeLabel(data)}
        </Typography>
        {switched && (
          <Chip
            label={subject === "prospect_order" ? "On order" : `On ${SUBJECT_LABELS[subject]}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.6rem" }}
          />
        )}
        {inactive && <Chip label="Off" size="small" sx={{ height: 18, fontSize: "0.6rem" }} />}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {summarize(data)}
      </Typography>

      {meta.hasOutput && (
        <Handle id="out" type="source" position={Position.Bottom} style={handleStyle(meta.accent)} />
      )}
    </Box>
  );
}
